import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

import { Sample } from '../../../../models/sample.model';
import { TranslateService } from '../../../../services/translate.service';
import {
    MultiSampleTablePresentationService,
    MultiSampleTablePresentation,
    MultiSampleTablePresentationLabels,
    MultiSampleTableGroupView,
    MultiSampleTaskAreaRowView
} from './multi-sample-table-presentation.service';

export interface MultiSampleSurvey {
    name: string;
    code: string;
    description: string;
    startDate?: string;
    endDate?: string;
    sampledBy?: string;
}

export interface MultiSampleData {
    survey?: MultiSampleSurvey;
    samples: Sample[];
}

export interface OelTypeData {
    oelTypeId: string;
    oelTypeName: string;
    oelValue: number;
    shiftLength?: number;
    isAdjusted?: boolean;
    columnKey: string;
}

export interface ProcessedSample {
    sampleNumber: string | number;
    segName: string;
    sampleCategory: string;
    startDate: string;
    startTime: string;
    duration: number;
    volume: number;
    respType: string | null;
    respAPF: number | string;
    mucExceeded: string | null;
    locationComments: string;
    rawSample: Sample;
    [key: string]: unknown;
}

@Component({
    standalone: true,
    selector: 'app-multi-sample-table-widget',
    imports: [],
    templateUrl: './multi-sample-table-widget.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './multi-sample-table-widget.component.scss'
})
export class MultiSampleTableWidgetComponent implements OnInit, OnChanges {
    @Input() data: MultiSampleData | null = null;
    @Input() showHeader = true;
    @Input() isPdfMode = false;
    @Input() excludeAdjustedOel = false;
    @Input() excludedOelColumnKeys: string[] = [];
    @Input() selectedOelFilters: (number | string)[] = [];

    presentation: MultiSampleTablePresentation | null = null;

    constructor(
        private presentationService: MultiSampleTablePresentationService,
        private translateService: TranslateService
    ) { }

    ngOnInit() { this.processData(); }
    ngOnChanges(changes: SimpleChanges) {
        if (changes['data'] || changes['excludeAdjustedOel'] || changes['excludedOelColumnKeys'] || changes['selectedOelFilters']) {
            this.processData();
        }
    }

    private processData(): void {
        this.presentation = this.presentationService.build(
            this.data,
            {
                selectedOelFilters: this.selectedOelFilters,
                excludeAdjustedOel: this.excludeAdjustedOel,
                excludedOelColumnKeys: this.excludedOelColumnKeys
            },
            this.buildPresentationLabels()
        );
    }

    get tableGroups(): MultiSampleTableGroupView[] {
        return this.presentation?.tableGroups ?? [];
    }

    get taskAreaRows(): MultiSampleTaskAreaRowView[] {
        return this.presentation?.taskAreaRows ?? [];
    }

    private buildPresentationLabels(): MultiSampleTablePresentationLabels {
        const oelHeaderLines = this.translateService.getDynamicTranslation('Occupational<br>Exposure<br>Limits [mg/m³]').split('<br>');
        return {
            concentrationsHeader: this.translateService.getDynamicTranslation('All Concentrations shown with individual units'),
            sampleNumber: this.translateService.getDynamicTranslation('Sample Number'),
            jobTitleArea: this.translateService.getDynamicTranslation('Job Title/Area'),
            oelHeaderLine1: oelHeaderLines[0] ?? '',
            oelHeaderLine2: oelHeaderLines[1] ?? '',
            oelHeaderLine3: oelHeaderLines[2] ?? '',
            sampleType: this.translateService.getDynamicTranslation('Sample Type'),
            respType: this.translateService.getDynamicTranslation('Resp. Type'),
            respApf: this.translateService.getDynamicTranslation('Resp. APF'),
            mucExceeded: this.translateService.getDynamicTranslation('MUC Exceeded'),
            startDate: this.translateService.getDynamicTranslation('Start Date'),
            startTime: this.translateService.getDynamicTranslation('Start Time'),
            durationMin: this.translateService.getDynamicTranslation('Duration (min)'),
            volumeLiters: this.translateService.getDynamicTranslation('Volume (Liters)'),
            taskArea: this.translateService.getDynamicTranslation('Task/Area'),
            noTaskDescription: this.translateService.getDynamicTranslation('No task description available')
        };
    }

    public trackByIndex = trackByIndex;
}
